import React from "react";
import { Modal, ModalBody } from "reactstrap";
import { Button } from "react-bootstrap";
import { FiX, FiInfo } from "react-icons/fi";
import moment from "moment/moment";
import { formatRupees } from "../../../utils/currency";

const PlanSwitchConfirmModal = ({
  isOpen,
  toggle,
  targetPlan,
  currentSubscription,
  scheduledNextPlan,
  onConfirm,
  isProcessing,
}) => {
  if (!targetPlan) return null;

  const currentEnd = currentSubscription?.currentPeriodEnd
    ? moment(currentSubscription.currentPeriodEnd).format("MMM D, YYYY")
    : null;

  const targetPrice = formatRupees(targetPlan.priceInPaise);
  const hasCurrent = !!currentSubscription;
  const hasScheduled = !!scheduledNextPlan;

  let title;
  let bullets;
  let primaryLabel;
  let cancelLabel;
  let showAuthNote = false;

  if (!hasCurrent) {
    title = `Subscribe to ${targetPlan.name}?`;
    bullets = [
      <>
        <strong>{targetPrice}</strong>/month — payment starts today.
      </>,
      <>{targetPlan.monthlyListings} credits per month</>,
    ];
    primaryLabel = `Subscribe`;
    cancelLabel = "Cancel";
  } else if (hasScheduled) {
    title = `Change your upcoming plan from ${scheduledNextPlan.planName} to ${targetPlan.name}?`;
    bullets = [
      <>
        Your <strong>{currentSubscription.planName}</strong> plan continues
        {currentEnd ? <> until <strong>{currentEnd}</strong></> : ""}
      </>,
      <>
        <strong>{targetPlan.name}</strong> starts
        {currentEnd ? <> on <strong>{currentEnd}</strong></> : " when your current plan ends"}
        {" "}
        (replacing the previously scheduled {scheduledNextPlan.planName})
      </>,
      <>
        You'll be charged <strong>{targetPrice}</strong>
        {currentEnd ? <> on <strong>{currentEnd}</strong></> : " when it starts"}
      </>,
      <>₹0 charged now</>,
    ];
    primaryLabel = `Switch to ${targetPlan.name}`;
    cancelLabel = `Keep ${scheduledNextPlan.planName} queued`;
    showAuthNote = true;
  } else {
    title = `Switch from ${currentSubscription.planName} to ${targetPlan.name}?`;
    bullets = [
      <>
        Your <strong>{currentSubscription.planName}</strong> plan continues
        {currentEnd ? <> until <strong>{currentEnd}</strong></> : ""}
      </>,
      <>
        <strong>{targetPlan.name}</strong> starts automatically
        {currentEnd ? <> on <strong>{currentEnd}</strong></> : " when your current plan ends"}
      </>,
      <>
        You'll be charged <strong>{targetPrice}</strong>
        {currentEnd ? <> on <strong>{currentEnd}</strong></> : " when it starts"}
        {" "}
        (not today)
      </>,
      <>₹0 charged now — just authorizing the mandate</>,
    ];
    primaryLabel = `Switch to ${targetPlan.name}`;
    cancelLabel = `Keep ${currentSubscription.planName}`;
    showAuthNote = true;
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      className="billing-confirm-modal"
    >
      <ModalBody>
        <div className="billing-confirm-header">
          <h4 className="billing-confirm-title">{title}</h4>
          <button
            type="button"
            className="billing-confirm-close"
            onClick={toggle}
            disabled={isProcessing}
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>

        <ul className="billing-confirm-bullets">
          {bullets.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>

        {showAuthNote && (
          <div className="billing-confirm-note">
            <FiInfo />
            <span>
              You'll be charged ₹0 today — Razorpay just verifies your payment
              method for the future charge.
            </span>
          </div>
        )}

        <div className="billing-confirm-actions">
          <Button
            className="billing-confirm-cancel-btn"
            onClick={toggle}
            disabled={isProcessing}
          >
            {cancelLabel}
          </Button>
          <Button
            className="billing-confirm-primary-btn"
            onClick={onConfirm}
            disabled={isProcessing}
          >
            {isProcessing ? "Opening..." : primaryLabel}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default PlanSwitchConfirmModal;
